import Container from '@amzn/awsui-components-react/polaris/container';
import { useLocalizedText } from '../hooks/localizedText';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  AmbassadorRegistrationForm,
  AmbassadorRegistrationFormValues,
} from '../components/ambassadors/ambassadorRegistrationForm';
import { featureIsEnabled, useFeatures } from '../hooks/features';
import { COMMUNITY_AMBASSADOR_FEATURE_FLAG, NAVIGATION_REDESIGN } from '../utils/constants';
import { useEffect, useState } from 'react';
import { useCreateAmbassador } from '../hooks/ambassadors/ambassadorCreation/createAmbassadorMutation';
import { IneligibilityReasons } from '@amzn/red-velvet-api';
import {
  AmbassadorRegistrationFailure,
  AmbassadorRegistrationSuccess,
} from '../components/ambassadors/ambassadorRegistrationResults';
import { getAuthAlias } from '../utils/auth';
import { ambassadorFormValuesToRequest } from '../utils/ambassadors';
import { Alert, AppLayout, HelpPanel, Link, Spinner } from '@amzn/awsui-components-react';
import { AmbassadorRegistrationHelp } from '../components/ambassadors/ambassadorRegistrationHelp';
import { publishKatalMetric } from '../components/katalAnalytics';
import { useUser } from '../hooks/user';
import { useGetAmbassador } from '../hooks/ambassadors/getAmbassador';
import { getHttpStatusCode } from '../utils/errorHelper';
import { AppOutletContext } from '../App';

/**
 * The registration Help panel, mirroring the codebase's *HelpPanel components (WaitlistInfoHelpPanel).
 * It reads its own header text rather than having it interpolated at the call site, because the page
 * publishes this panel into the shared AppLayout from an effect keyed on the nav flag: an element that
 * had already resolved t() would keep whatever string the first render produced, and the ARB pack
 * arrives after it.
 */
function AmbassadorRegistrationHelpPanel() {
  const { t } = useLocalizedText(['ambassadors', 'translation']);

  return (
    <HelpPanel header={<h2>{t('ambassadorRegistration.help.title')}</h2>}>
      <AmbassadorRegistrationHelp />
    </HelpPanel>
  );
}

export function AmbassadorRegistration() {
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [successfulRegistration, setSuccessfulRegistration] = useState(false);
  const [ineligibilityReasons, setIneligibilityReasons] = useState<
    IneligibilityReasons | undefined
  >();

  const [formValues, setFormValues] = useState<AmbassadorRegistrationFormValues>({
    country: null,
    city: null,
    siteCode: null,
    businessUnit: null,
    siteRepresentation: false,
    teamRepresentation: false,
    employeeLedGroupRepresentation: false,
    howDidYouHearSpecification: '',
    howDidYouHear: null,
    myManagerHasApprovedThisRole: false,
    teamLocation: [],
    ergLocation: [],
    representedSiteCodes: [],
    ergName: null,
    representMultipleSites: false,
    sendMeACopy: false,
  });

  const { t } = useLocalizedText(['ambassadors', 'translation']);
  const createAmbassador = useCreateAmbassador();
  const user = useUser();
  const {
    data: ambassadorData,
    isLoading: isLoadingAmbassador,
    isError,
    error: ambassadorError,
  } = useGetAmbassador(user.employeeId || '');
  const isNotFound = getHttpStatusCode(ambassadorError) === 404;
  const hasData = ambassadorData !== undefined;
  const isAlreadyRegistered = ambassadorData?.ambassador?.isRegistered === true;

  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams);
  const isCommunityAmbassadorEnabled = featureIsEnabled(
    COMMUNITY_AMBASSADOR_FEATURE_FLAG,
    features,
    searchParams
  );
  const outletContext = useOutletContext<AppOutletContext | null>();

  const toolsPanel = <AmbassadorRegistrationHelpPanel />;

  // Set tools panel content when in new nav mode (user can open/close freely).
  // Deliberately NOT calling setToolsOpen(true) here — previous behavior auto-opened the panel
  // on mount, which fought with the user closing it (AppLayout would reopen it on re-render).
  // Now the user opens it themselves via the "i" icon in the AppLayout toolbar.
  //
  // Gated on the community-ambassador flag too: when it's off this page renders <></>, so
  // publishing the tools panel would populate the shared AppLayout's tools slot (and show the
  // "i" icon) for a page with no content.
  const shouldPublishTools = isNewNav && isCommunityAmbassadorEnabled;
  useEffect(() => {
    if (shouldPublishTools && outletContext) {
      outletContext.setTools(toolsPanel);
    }
    return () => {
      if (shouldPublishTools && outletContext) {
        outletContext.setTools(null);
        outletContext.setToolsOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPublishTools]);

  const getActiveUserAlias = (): string | undefined => {
    return getAuthAlias() ?? undefined;
  };

  const submitRegistration = async () => {
    const registerRequest = ambassadorFormValuesToRequest(formValues, getActiveUserAlias());
    createAmbassador.mutate(registerRequest, {
      onSuccess: (data) => {
        publishKatalMetric('update_ambassador_success', JSON.stringify(registerRequest));
        setHasSubmitted(true);
        setSuccessfulRegistration(data.result.isSuccess);
        if (data.result.ineligibilityReasons) {
          setIneligibilityReasons(data.result.ineligibilityReasons);
        }
      },
      onError: (err) => {
        publishKatalMetric(
          'update_ambassador_error',
          JSON.stringify({ ...registerRequest, error: err?.message })
        );
        console.error(err);
      },
    });
  };

  if (isCommunityAmbassadorEnabled) {
    const content = (
      <Container fitHeight variant="stacked">
        {!hasSubmitted ? (
          <>
            <h1 data-testid="ambassador-registration-header">
              {t('ambassadorRegistration.title')}
            </h1>
            {isLoadingAmbassador || (!hasData && !isError) ? (
              <Spinner />
            ) : isError && !isNotFound ? (
              <Alert type="error">{t('ambassadorRegistration.loadFailed')}</Alert>
            ) : isAlreadyRegistered ? (
              <Alert type="info" header={t('ambassadorRegistration.alreadyRegistered.header')}>
                {t('ambassadorRegistration.alreadyRegistered.message')}{' '}
                <Link href="/ambassadors">
                  {t('ambassadorRegistration.alreadyRegistered.viewDirectory')}
                </Link>
              </Alert>
            ) : (
              <AmbassadorRegistrationForm
                formValues={formValues}
                setFormValues={setFormValues}
                onSubmit={submitRegistration}
                loading={createAmbassador.isPending}
              />
            )}
          </>
        ) : (
          <>
            {successfulRegistration ? (
              <AmbassadorRegistrationSuccess />
            ) : (
              <AmbassadorRegistrationFailure ineligibilityReasons={ineligibilityReasons} />
            )}
          </>
        )}
      </Container>
    );

    // New navigation: return content only, shell handled by root AppLayout
    if (isNewNav) {
      return content;
    }

    // Legacy: keep existing AppLayout wrapper
    return <AppLayout navigationHide toolsOpen={true} tools={toolsPanel} content={content} />;
  }
  return <></>;
}
